import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiAward,
  FiCopy,
  FiEdit2,
  FiEye,
  FiMessageSquare,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiTrash2,
  FiUser,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { FaCrown } from 'react-icons/fa6';
import { PRIMARY } from '../../theme';
import api from '../../utils/api';
import {
  PERMISSIONS,
  canFeatureForRole,
  hasAnyPermissionForModule,
} from '../../utils/permissions';
import { useRolePermissionMatrix } from '../../hooks/useRolePermissionMatrix';
import { useReferenceData } from '../../contexts/ReferenceDataContext';
import Pagination from '../../components/common/Pagination';
import StyledSelectDropdown from '../../components/common/StyledSelectDropdown';
import ConfirmActionModal from '../../components/modals/ConfirmActionModal';
import '../../styles/Modals.css';
import { useDebounce } from '../../hooks/useDebounce';
import { getDefaultItemsPerPageSync, getDefaultItemsPerPage } from '../../utils/settings';
import {
  getRoleIconComponent,
  TEAM_MEMBER_MODAL_ACCENT,
  TEAM_MEMBER_MODAL_HEADER_TILE_BG,
  TEAM_MEMBER_MODAL_ICON_TILE_BG,
} from '../../utils/teamMemberDetailModalUi';

const FEEDBACK_PENDING_API_VALUE = 'Feedback pending';
const TEAMS_PURPLE = 'rgb(102, 126, 234)';
const TEAMS_TOOLBAR_BORDER = '#dee2e6';
const TEAMS_CARD_SHADOW = '0 4px 24px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.08)';
const SORTABLE_COLUMNS = [
  'teamName',
  'teamLeader',
  'startDate',
  'startTime',
  'endDate',
  'endTime',
  'avgRating',
];

const TEAMS_EVENTS_DEFAULT_TABLE_FILTERS = {
  searchTerm: '',
  leaderFilter: '',
  memberFilter: '',
  showOwnTeamsOnFirstLoad: null,
};

const normalizeFeedbackStatus = (value) =>
  String(value || '')
    .trim()
    .toLowerCase();

const normalizeJobType = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[\s/_-]/g, '');

const toIdString = (value) => {
  if (!value) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object' && value._id) return String(value._id);
  if (typeof value === 'object' && value.id) return String(value.id);
  return '';
};

const getUserDirectoryDisplayName = (user) =>
  String(user?.name || user?.fullName || user?.userName || user?.username || user?.email || '').trim();

const toDateTime = (dateValue, timeValue) => {
  if (!dateValue) return null;
  const safeTime = timeValue && String(timeValue).trim() ? String(timeValue).trim() : '00:00';
  const parsed = new Date(`${dateValue}T${safeTime}`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const isEventEditable = (row) => true;

const canDeleteEvent = (row) => true;

const canGiveFeedback = (row) => true;

const hasSubmittedFeedback = (row) => {
  const normalizedFeedbackStatus = normalizeFeedbackStatus(row?.feedbackStatus);
  return normalizedFeedbackStatus === 'completed' || normalizedFeedbackStatus === 'complete';
};

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const round1 = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round((n + Number.EPSILON) * 10) / 10;
};

const toDisplayRatingOutOfFive = (rawValue, rawItem) => {
  const rating = toFiniteNumber(rawValue);
  if (rating === null || rating < 0) return null;

  const configuredScale = toFiniteNumber(
    rawItem?.ratingOutOf ??
      rawItem?.overallOutOf ??
      rawItem?.maxRating ??
      rawItem?.ratingScale ??
      rawItem?.scaleMax
  );

  if (configuredScale && configuredScale > 0 && configuredScale !== 5) {
    return (rating / configuredScale) * 5;
  }

  // Backend can send normalized overall rating (0..1). Display it consistently as /5.
  if (rating > 0 && rating <= 1) {
    return rating * 5;
  }

  return rating;
};

/**
 * Uses `averageTotal` from GET /event-teams when present (root or common nested shapes).
 * Only if missing do we fall back to other summary fields — avoids mixing decimals from
 * another field with the API’s authoritative averageTotal.
 */
const resolveAverageRatingFromItem = (item) => {
  const averageTotalSources = [
    item?.averageTotal,
    item?.average_total,
    item?.feedback?.averageTotal,
    item?.summary?.averageTotal,
    item?.teamFeedback?.averageTotal,
    item?.feedbackSummary?.averageTotal,
  ];
  for (const value of averageTotalSources) {
    const n = toFiniteNumber(value);
    if (n !== null && n >= 0) return n;
  }

  const fallbackCandidates = [
    item?.averageRating,
    item?.overallRating,
    item?.teamOverallRating,
    item?.avgRating,
    item?.rating,
    item?.avg,
    item?.average,
  ];
  const parsedValues = fallbackCandidates
    .map((value) => toFiniteNumber(value))
    .filter((value) => Number.isFinite(value) && value >= 0);
  if (parsedValues.length === 0) return null;

  const preciseValue = parsedValues.find((value) => !Number.isInteger(value));
  return preciseValue ?? parsedValues[0];
};

const normalizeEventTeamRows = (eventTeamsResponse) => {
  const rawRows = Array.isArray(eventTeamsResponse?.data)
    ? eventTeamsResponse.data
    : Array.isArray(eventTeamsResponse)
    ? eventTeamsResponse
    : [];

  return rawRows.map((item, index) => ({
    id: item.id || item._id || index + 1,
    eventTeamId: item._id || item.id || '',
    eventName: item.eventName || item.event || '-',
    teamName: item.teamName || item.team || '-',
    teamLeader:
      item.teamLeaderName ||
      item.teamLeader?.name ||
      item.teamLeader?.fullName ||
      item.teamLeader ||
      '-',
    teamLeaderId:
      item.teamLeaderId ||
      (typeof item.teamLeader === 'string' ? item.teamLeader : '') ||
      item.teamLeader?._id ||
      item.teamLeader?.id ||
      '',
    startDate: item.startDate || '',
    endDate: item.endDate || '',
    startTime: item.startTime || '',
    endTime: item.endTime || '',
    status: item.status || 'Not Started',
    feedbackStatus:
      item.feedbackStatus ||
      item.feedback?.status ||
      item.eventFeedbackStatus ||
      item.feedback_state ||
      '',
    avgRating: toDisplayRatingOutOfFive(resolveAverageRatingFromItem(item), item),
    teamMembers: Array.isArray(item.teamMembers)
      ? item.teamMembers
      : Array.isArray(item.members)
      ? item.members
      : [],
  }));
};

const getPaginationMeta = (eventTeamsResponse) => {
  const payload = eventTeamsResponse?.data || eventTeamsResponse || {};
  const candidates = [
    payload?.pagination,
    payload?.meta,
    eventTeamsResponse?.pagination,
    eventTeamsResponse?.meta,
    payload,
    eventTeamsResponse,
  ];

  const pickNumber = (keys) => {
    for (const source of candidates) {
      if (!source || typeof source !== 'object') continue;
      for (const key of keys) {
        const value = Number(source[key]);
        if (Number.isFinite(value) && value >= 0) return value;
      }
    }
    return null;
  };

  return {
    totalItems: pickNumber(['total', 'totalItems', 'totalCount', 'count', 'totalDocs']),
    totalPages: pickNumber(['totalPages', 'pages', 'pageCount']),
    currentPage: pickNumber(['page', 'currentPage']),
  };
};

const TeamsEventsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(sessionStorage.getItem('user') || '{}');
    } catch (error) {
      return {};
    }
  }, []);
  const isAdminUser = currentUser?.role === 'admin';
  const appBasePath = isAdminUser ? '/admin' : '/employee';
  const teamsEventsFiltersStorageKey = `${appBasePath}:teams-events:table-filters`;
  const initialSavedTableFilters = useMemo(() => {
    try {
      const raw = sessionStorage.getItem(teamsEventsFiltersStorageKey);
      if (!raw) return TEAMS_EVENTS_DEFAULT_TABLE_FILTERS;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') {
        return TEAMS_EVENTS_DEFAULT_TABLE_FILTERS;
      }
      return { ...TEAMS_EVENTS_DEFAULT_TABLE_FILTERS, ...parsed };
    } catch (error) {
      return TEAMS_EVENTS_DEFAULT_TABLE_FILTERS;
    }
  }, [teamsEventsFiltersStorageKey]);
  const hasSavedTableFiltersRef = useRef(
    Boolean(
      initialSavedTableFilters.searchTerm ||
      initialSavedTableFilters.leaderFilter ||
      initialSavedTableFilters.memberFilter
    )
  );
  const [searchTerm, setSearchTerm] = useState(initialSavedTableFilters.searchTerm);
  const debouncedSearchTerm = useDebounce(searchTerm, 400);
  const [leaderFilter, setLeaderFilter] = useState(initialSavedTableFilters.leaderFilter);
  const [memberFilter, setMemberFilter] = useState(initialSavedTableFilters.memberFilter);
  const [dateRangeFilter, setDateRangeFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(getDefaultItemsPerPageSync());
  const [totalItems, setTotalItems] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [rows, setRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: 'teamName',
    direction: 'asc',
  });
  const [users, setUsers] = useState([]);
  const [usersReady, setUsersReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableRefreshing, setTableRefreshing] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { rolePermissions, permissionsReady } = useRolePermissionMatrix();
  const { loading: referenceDataLoading } = useReferenceData();
  const lastEventTeamsListKeyRef = useRef('');
  const loadEventTeamsRef = useRef(null);
  const hasAppliedDefaultEmployeeFilterRef = useRef(false);
  const [showOwnTeamsOnFirstLoad, setShowOwnTeamsOnFirstLoad] = useState(() => {
    if (typeof initialSavedTableFilters.showOwnTeamsOnFirstLoad === 'boolean') {
      return initialSavedTableFilters.showOwnTeamsOnFirstLoad;
    }
    return !isAdminUser;
  });
  const normalizedUserRole = String(currentUser?.role || '').trim().toLowerCase();
  const currentUserIds = useMemo(
    () =>
      new Set(
        [
          currentUser?.id,
          currentUser?._id,
          currentUser?.userId,
          currentUser?.employeeId,
          currentUser?.employee?._id,
          currentUser?.employee?.id,
        ]
          .filter(Boolean)
          .map((value) => String(value))
      ),
    [currentUser]
  );

  /** Prefer role matrix from Settings → User Roles; fall back to `can()` / static role map. */
  const canUseFeature = useCallback(
    (possibleKeys = []) => canFeatureForRole(normalizedUserRole, rolePermissions, possibleKeys),
    [rolePermissions, normalizedUserRole]
  );
  const isCurrentUserTeamLeader = useMemo(() => {
    const me = users.find((u) => currentUserIds.has(String(u.id || u._id || u.userId || '')));
    const userDirectorySaysLeader = Boolean(
      me && normalizeJobType(me.role) === normalizeJobType('Team Leader')
    );
    const tokenUserSaysLeader =
      normalizeJobType(currentUser?.role) === normalizeJobType('Team Leader');
    return userDirectorySaysLeader || tokenUserSaysLeader;
  }, [users, currentUserIds, currentUser?.role]);

  const isEmployeeUser = normalizedUserRole === 'employee';
  const hasEditTeamPermission = canUseFeature([PERMISSIONS.EDIT_TEAM]);
  const hasDeleteTeamPermission = canUseFeature([
    PERMISSIONS.DELETE_TEAM,
    'delete_event_team',
    'remove_team',
    'remove_event_team',
    'delete_team_event',
    'remove_team_event',
  ]);

  // Add / edit / copy / delete / feedback flags come from Settings → User Roles.
  const canAddTeamPermission = canUseFeature([PERMISSIONS.ADD_TEAM]);
  const canCopyTeamPermission = canUseFeature([PERMISSIONS.ADD_TEAM]);
  const canViewTeamDetails = useMemo(
    () => hasAnyPermissionForModule(normalizedUserRole, rolePermissions, 'Teams'),
    [normalizedUserRole, rolePermissions]
  );
  const canEditTeamPermission = hasEditTeamPermission;
  const canDeleteTeamPermission = hasDeleteTeamPermission;
  const canViewFeedbackPermission = canUseFeature([
    PERMISSIONS.VIEW_TEAM_EVENT_FEEDBACK,
    PERMISSIONS.VIEW_TEAM_FEEDBACK,
    'view_team_event_feedback',
  ]);
  const hasShowAllTeamsPermission = canUseFeature([PERMISSIONS.SHOW_ALL_TEAMS, 'show_all_teams']);

  const loadEventTeams = useCallback(async (queryParams = {}) => {
    try {
      const hasVisibleRows = rows.length > 0;
      if (hasVisibleRows) {
        setTableRefreshing(true);
      } else {
        setLoading(true);
      }
      const requestParams = {
        ...queryParams,
        page: queryParams.page ?? currentPage,
        limit: queryParams.limit ?? itemsPerPage,
      };
      const eventTeamsResponse = await api.getEventTeams(requestParams);
      const normalizedRows = normalizeEventTeamRows(eventTeamsResponse);

      const paginationMeta = getPaginationMeta(eventTeamsResponse);

      setRows(normalizedRows);
      if (Number.isFinite(paginationMeta.totalItems)) {
        setTotalItems(paginationMeta.totalItems);
      } else if (Number.isFinite(paginationMeta.totalPages)) {
        setTotalItems(paginationMeta.totalPages * (requestParams.limit || itemsPerPage));
      } else {
        setTotalItems(normalizedRows.length);
      }
      if (
        Number.isFinite(paginationMeta.currentPage) &&
        paginationMeta.currentPage > 0 &&
        paginationMeta.currentPage !== currentPage
      ) {
        setCurrentPage(paginationMeta.currentPage);
      }
    } catch (error) {
      console.error('Error fetching event teams:', error);
      toast.error(error.message || 'Unable to fetch event teams');
    } finally {
      setLoading(false);
      setTableRefreshing(false);
    }
  }, [currentPage, itemsPerPage, rows.length]);

  loadEventTeamsRef.current = loadEventTeams;

  // Fetch settings on mount and update itemsPerPage if needed
  useEffect(() => {
    const updateItemsPerPage = async () => {
      const defaultItems = await getDefaultItemsPerPage();
      if (defaultItems !== itemsPerPage) {
        setItemsPerPage(defaultItems);
      }
    };
    updateItemsPerPage();
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersResponse = await api.getUsers({ isActive: 'true', limit: 1000 });
        const userRows = Array.isArray(usersResponse?.data)
          ? usersResponse.data
          : Array.isArray(usersResponse?.data?.users)
          ? usersResponse.data.users
          : Array.isArray(usersResponse?.users)
          ? usersResponse.users
          : [];
        setUsers(userRows);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setUsersReady(true);
      }
    };
    loadUsers();
  }, []);

  const usersById = useMemo(() => {
    const map = {};
    users.forEach((user) => {
      const key = String(user.id || user._id || '');
      if (key) map[key] = user;
    });
    return map;
  }, [users]);

  const resolveMemberName = useCallback((member) => {
    const candidateName =
      member.member?.name ||
      member.member?.fullName ||
      member.member?.userName ||
      member.employee?.name ||
      member.employee?.fullName ||
      member.employee?.userName ||
      member.userName ||
      member.name ||
      member.user?.name ||
      member.user?.fullName;
    if (candidateName) return candidateName;

    const userId =
      member.member?._id ||
      member.member?.id ||
      member.employee?._id ||
      member.employee?.id ||
      member.userId ||
      member.memberId ||
      member.id ||
      member.user?._id ||
      member.user?.id;
    if (!userId) return '-';

    const matchedUser = usersById[String(userId)];
    if (!matchedUser) return String(userId);
    return (
      matchedUser.name ||
      matchedUser.fullName ||
      matchedUser.userName ||
      matchedUser.username ||
      matchedUser.email ||
      String(userId)
    );
  }, [usersById]);

  const leaders = useMemo(() => {
    return Array.from(
      new Set(
        users
          .filter((u) => normalizeJobType(u.role) === normalizeJobType('Team Leader'))
          .map((u) => getUserDirectoryDisplayName(u))
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b));
  }, [users]);

  const currentUserDisplayName = useMemo(() => {
    const me = users.find((u) => currentUserIds.has(String(u.id || u._id || u.userId || '')));
    return getUserDirectoryDisplayName(me || currentUser);
  }, [users, currentUserIds, currentUser]);

  const members = useMemo(() => {
    const memberNamesFromUsers = users
      .map((user) => getUserDirectoryDisplayName(user))
      .filter(Boolean);

    const memberNamesFromRows = rows
      .flatMap((row) => row.teamMembers || [])
      .map((member) => resolveMemberName(member))
      .filter((name) => name && name !== '-');

    // Keep selected values visible even if current result-set is narrow.
    const stickySelections = [memberFilter, leaderFilter].filter(Boolean);

    return Array.from(
      new Set([...memberNamesFromUsers, ...memberNamesFromRows, ...stickySelections])
    ).sort((a, b) => a.localeCompare(b));
  }, [users, rows, resolveMemberName, memberFilter, leaderFilter]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        teamsEventsFiltersStorageKey,
        JSON.stringify({
          searchTerm,
          leaderFilter,
          memberFilter,
          showOwnTeamsOnFirstLoad,
        })
      );
    } catch (error) {
      // Ignore storage write failures.
    }
  }, [
    searchTerm,
    leaderFilter,
    memberFilter,
    showOwnTeamsOnFirstLoad,
    teamsEventsFiltersStorageKey,
  ]);

  useEffect(() => {
    return () => {
      const nextPath = String(window.location.pathname || '').toLowerCase();
      const basePath = appBasePath.toLowerCase();
      const shouldKeepFilters =
        nextPath.startsWith(`${basePath}/teams-events`) ||
        nextPath.startsWith(`${basePath}/edit-team`) ||
        nextPath.startsWith(`${basePath}/copy-team`) ||
        nextPath.startsWith(`${basePath}/add-team`) ||
        nextPath.includes('/feedback');
      if (shouldKeepFilters) return;
      try {
        sessionStorage.removeItem(teamsEventsFiltersStorageKey);
      } catch (error) {
        // Ignore storage delete failures.
      }
    };
  }, [appBasePath, teamsEventsFiltersStorageKey, location.pathname]);

  useEffect(() => {
    if (hasSavedTableFiltersRef.current) {
      hasAppliedDefaultEmployeeFilterRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (isAdminUser || !usersReady || hasAppliedDefaultEmployeeFilterRef.current) return;
    const myName = String(currentUserDisplayName || '').trim();
    if (!myName) return;

    hasAppliedDefaultEmployeeFilterRef.current = true;
    setShowOwnTeamsOnFirstLoad(false);
    setCurrentPage(1);

    if (isCurrentUserTeamLeader) {
      setLeaderFilter(myName);
      setMemberFilter('');
      return;
    }

    setMemberFilter(myName);
    setLeaderFilter('');
  }, [isAdminUser, usersReady, currentUserDisplayName, isCurrentUserTeamLeader]);

  const showAvgRatingColumn = isAdminUser || (usersReady && isCurrentUserTeamLeader);

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getStatusStyle = (status) => {
    switch (status) {
      case 'Completed':
        return { background: '#dcfce7', color: '#166534' };
      case FEEDBACK_PENDING_API_VALUE:
      case 'Feedback Pending':
        return { background: '#fef3c7', color: '#92400e' };
      case 'Started':
      case 'In Progress':
        return { background: '#dbeafe', color: '#1e40af' };
      default:
        return { background: '#f1f5f9', color: '#334155' };
    }
  };

  const formatTime = (value) => {
    if (!value) return '-';
    const parsed = new Date(`1970-01-01T${value}`);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getSortableValue = (row, key) => {
    if (key === 'teamName' || key === 'teamLeader') {
      return String(row[key] || '').trim().toLowerCase();
    }

    if (key === 'avgRating') {
      return Number.isFinite(row.avgRating) ? row.avgRating : Number.NEGATIVE_INFINITY;
    }

    if (key === 'startDate' || key === 'endDate') {
      if (!row[key]) return Number.NEGATIVE_INFINITY;
      const parsedDate = new Date(row[key]).getTime();
      return Number.isNaN(parsedDate) ? Number.NEGATIVE_INFINITY : parsedDate;
    }

    if (key === 'startTime' || key === 'endTime') {
      if (!row[key]) return Number.NEGATIVE_INFINITY;
      const parsedTime = new Date(`1970-01-01T${row[key]}`).getTime();
      return Number.isNaN(parsedTime) ? Number.NEGATIVE_INFINITY : parsedTime;
    }

    return '';
  };

  const getMemberIdentifiers = useCallback((member) => {
    return [
      member.member,
      member.member?._id,
      member.member?.id,
      member.employee?._id,
      member.employee?.id,
      member.userId,
      member.memberId,
      member.user,
      member.id,
      member.user?._id,
      member.user?.id,
    ]
      .map((value) => toIdString(value))
      .filter(Boolean);
  }, []);

  const isCurrentUserParticipant = useCallback(
    (row) => {
      if (isAdminUser) return true;

      const leaderIds = [row?.teamLeaderId].map((value) => toIdString(value)).filter(Boolean);
      if (leaderIds.some((id) => currentUserIds.has(id))) return true;

      const memberIds = (row?.teamMembers || []).flatMap((member) => getMemberIdentifiers(member));
      if (memberIds.some((id) => currentUserIds.has(id))) return true;

      return false;
    },
    [currentUserIds, getMemberIdentifiers, isAdminUser]
  );

  const canManageEventFeedback = useCallback(
    (row) => {
      if (isAdminUser) return true;

      const leaderIds = [row?.teamLeaderId].map((value) => toIdString(value)).filter(Boolean);
      if (leaderIds.some((id) => currentUserIds.has(id))) return true;

      return false;
    },
    [isAdminUser, currentUserIds]
  );

  const canAccessFeedbackForRow = useCallback(
    (row) => {
      if (!isCurrentUserParticipant(row)) return false;
      return canViewFeedbackPermission;
    },
    [isCurrentUserParticipant, canViewFeedbackPermission]
  );

  const canEditEvent = useCallback(
    (row) =>
      canEditTeamPermission &&
      isEventEditable(row) &&
      (isAdminUser || canManageEventFeedback(row) || isCurrentUserParticipant(row)),
    [canEditTeamPermission, canManageEventFeedback, isCurrentUserParticipant, isAdminUser]
  );

  const filteredRows = useMemo(() => {
    if (!isAdminUser && showOwnTeamsOnFirstLoad && !hasShowAllTeamsPermission) {
      return rows.filter((row) => isCurrentUserParticipant(row));
    }
    return rows;
  }, [rows, isAdminUser, showOwnTeamsOnFirstLoad, hasShowAllTeamsPermission, isCurrentUserParticipant]);

  const sortedRows = useMemo(() => {
    if (!SORTABLE_COLUMNS.includes(sortConfig.key)) {
      return filteredRows;
    }

    const sorted = [...filteredRows].sort((a, b) => {
      const aValue = getSortableValue(a, sortConfig.key);
      const bValue = getSortableValue(b, sortConfig.key);

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredRows, sortConfig]);

  const effectiveTotalItems = Number.isFinite(totalItems) && totalItems > 0 ? totalItems : sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(effectiveTotalItems / itemsPerPage));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedRows = sortedRows;

  const tableColumnCount = showAvgRatingColumn ? 5 : 4;

  useEffect(() => {
    if (!showAvgRatingColumn && sortConfig.key === 'avgRating') {
      setSortConfig({ key: 'teamName', direction: 'asc' });
    }
  }, [showAvgRatingColumn, sortConfig.key]);

  const handleSort = (key) => {
    if (!SORTABLE_COLUMNS.includes(key)) return;
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  const renderSortableHeader = (key, label, title) => {
    const isActive = sortConfig.key === key;
    const leftAlignedColumns = ['teamName', 'teamLeader'];
    const textAlign = leftAlignedColumns.includes(key) ? 'left' : 'center';
    return (
      <th
        style={{
          textAlign,
          padding: '8px 10px',
          color: '#ffffff',
          fontSize: '12px',
          fontWeight: 600,
          cursor: 'pointer',
          userSelect: 'none',
        }}
        onClick={() => handleSort(key)}
        title={title}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <span style={{ opacity: isActive ? 1 : 0.95 }}>{label}</span>
          <span style={{ color: isActive ? '#e9d5ff' : 'rgba(255,255,255,0.65)', fontSize: '10px' }}>
            {getSortIcon(key)}
          </span>
        </span>
      </th>
    );
  };

  const buildFilterParams = useCallback(() => {
    const params = {};
    const findUserIdByDisplayName = (displayName) => {
      const normalized = String(displayName || '').trim().toLowerCase();
      if (!normalized) return '';
      const matched = users.find(
        (u) => getUserDirectoryDisplayName(u).trim().toLowerCase() === normalized
      );
      return matched ? String(matched.id || matched._id || '') : '';
    };

    if (debouncedSearchTerm.trim()) params.teamName = debouncedSearchTerm.trim();
    if (leaderFilter) {
      const leaderId = findUserIdByDisplayName(leaderFilter);
      if (leaderId) params.teamLeaderId = leaderId;
      else params.teamLeaderName = leaderFilter;
    }
    if (memberFilter) {
      const memberId = findUserIdByDisplayName(memberFilter);
      if (memberId) params.teamMemberId = memberId;
      else params.teamMemberName = memberFilter;
    }
    return params;
  }, [debouncedSearchTerm, leaderFilter, memberFilter, users]);

  useEffect(() => {
    if (!isAdminUser && !usersReady) return;

    const nextFilters = buildFilterParams();
    const payload = {
      ...nextFilters,
      page: 1,
      limit: itemsPerPage,
      sortBy: sortConfig.key,
      sortOrder: sortConfig.direction,
    };
    const key = JSON.stringify(payload);
    if (key === lastEventTeamsListKeyRef.current) return;
    lastEventTeamsListKeyRef.current = key;

    setAppliedFilters(nextFilters);
    setCurrentPage(1);
    loadEventTeamsRef.current?.(payload);
  }, [
    isAdminUser,
    usersReady,
    users,
    debouncedSearchTerm,
    leaderFilter,
    memberFilter,
    itemsPerPage,
    sortConfig.key,
    sortConfig.direction,
    buildFilterParams,
  ]);

  const handleReset = () => {
    setSearchTerm('');
    setLeaderFilter('');
    setMemberFilter('');
    setShowOwnTeamsOnFirstLoad(false);
    setDateRangeFilter('all');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    setAppliedFilters({});
    lastEventTeamsListKeyRef.current = '';
  };

  const handleDelete = (row) => {
    if (!canDeleteTeamPermission) {
      toast.error('You do not have permission to delete team events');
      return;
    }
    setDeleteCandidate(row);
  };

  const confirmDelete = async () => {
    if (!deleteCandidate) return;

    const eventTeamId = deleteCandidate.eventTeamId || deleteCandidate.id;
    if (!eventTeamId) {
      toast.error('Event team id not found');
      return;
    }

    try {
      setDeleteLoading(true);
      await api.deleteEventTeam(eventTeamId);
      setRows((prev) =>
        prev.filter(
          (item) =>
            String(item.eventTeamId || item.id) !== String(eventTeamId)
        )
      );
      toast.success('Event team deleted successfully');
      if (selectedTeam && String(selectedTeam.eventTeamId || selectedTeam.id) === String(eventTeamId)) {
        setSelectedTeam(null);
      }
      setDeleteCandidate(null);
      setTotalItems((prev) => (Number.isFinite(prev) ? Math.max(0, prev - 1) : 0));
    } catch (error) {
      console.error('Error deleting event team:', error);
      toast.error(error.message || 'Failed to delete event team');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEditClick = (row) => {
    if (!canEditTeamPermission) {
      toast.error('You do not have permission to edit team events');
      return;
    }

    navigate(`${appBasePath}/edit-team`, { state: { editEventTeam: row } });
  };

  const handleViewTeamClick = (row) => {
    if (!canViewTeamDetails) {
      toast.error('You do not have access to team details');
      return;
    }
    setSelectedTeam(row);
  };

  const handleDuplicateClick = (row) => {
    if (!canCopyTeamPermission) {
      toast.error('You do not have permission to copy team events');
      return;
    }
    navigate(`${appBasePath}/copy-team`, {
      state: {
        eventTeam: {
          ...row,
          eventName: row.eventName || '',
        },
      },
    });
  };

  const handleFeedbackClick = async (row) => {
    if (!canAccessFeedbackForRow(row)) {
      toast.error('You do not have access to this event feedback');
      return;
    }
    if (!canViewFeedbackPermission) {
      toast.error('You do not have permission to view feedback');
      return;
    }

    const eventTeamId = row.eventTeamId || row.id;
    if (!eventTeamId) {
      toast.error('Event team id not found');
      return;
    }

    navigate(`${appBasePath}/teams-events/${eventTeamId}/view-feedback`);
  };

  const canUseFeedbackAction = useCallback(
    (row) => {
      return canAccessFeedbackForRow(row) && canViewFeedbackPermission;
    },
    [canAccessFeedbackForRow, canViewFeedbackPermission]
  );

  const getFeedbackButtonLabel = () => 'View Feedback';

  const toolbarPrimaryBtnBase = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
    height: 34,
    padding: '0 14px',
    border: 'none',
    color: '#ffffff',
    borderRadius: 6,
    cursor: 'pointer',
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    boxShadow: '0 1px 2px rgba(78, 115, 223, 0.28)',
  };

  if (!permissionsReady || referenceDataLoading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
        Loading…
      </div>
    );
  }

  if (!hasAnyPermissionForModule(normalizedUserRole, rolePermissions, 'Teams')) {
    return <Navigate to={`${appBasePath}/dashboard`} replace />;
  }

  return (
    <div style={{ padding: '16px', background: 'transparent', minHeight: '100%' }}>
      <div
        style={{
          background: 'white',
          borderRadius: 12,
          boxShadow: TEAMS_CARD_SHADOW,
          border: '1px solid #eaecf0',
          padding: '18px 20px 20px',
        }}
      >
        <div style={{ width: '100%', overflowX: 'auto' }}>
          <div
            style={{
              display: 'flex',
              flexWrap: 'nowrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              columnGap: 0,
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                gap: 8,
                flex: '1 1 auto',
                minWidth: 0,
                justifyContent: 'flex-start',
              }}
            >
            <div
              style={{
                position: 'relative',
                flex: '0 0 auto',
                width: 188,
              }}
            >
              <FiSearch
                size={14}
                aria-hidden
                style={{
                  position: 'absolute',
                  left: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#98a2b3',
                  pointerEvents: 'none',
                }}
              />
              <input
                type="text"
                placeholder="Search Team name"
                value={searchTerm}
                onChange={(e) => {
                  setShowOwnTeamsOnFirstLoad(false);
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  height: 34,
                  padding: '0 10px 0 32px',
                  border: `1px solid ${TEAMS_TOOLBAR_BORDER}`,
                  borderRadius: 6,
                  fontSize: 12,
                  color: '#344054',
                  background: '#fff',
                }}
              />
            </div>
            <div style={{ flex: '0 0 auto', width: 172, minWidth: 0 }}>
              <StyledSelectDropdown
                ariaLabel="Filter by team leader"
                placeholder="All Team Leaders"
                emptyOptionLabel="All Team Leaders"
                value={leaderFilter}
                onChange={(v) => {
                  setShowOwnTeamsOnFirstLoad(false);
                  setLeaderFilter(v);
                  setCurrentPage(1);
                }}
                options={leaders.map((leader) => ({
                  value: leader,
                  label: leader,
                }))}
                leftIcon={<FaCrown size={13} style={{ color: TEAMS_PURPLE, flexShrink: 0 }} aria-hidden />}
              />
            </div>
            <div style={{ flex: '0 0 auto', width: 172, minWidth: 0 }}>
              <StyledSelectDropdown
                ariaLabel="Filter by member"
                placeholder="All Members"
                emptyOptionLabel="All Members"
                value={memberFilter}
                onChange={(v) => {
                  setShowOwnTeamsOnFirstLoad(false);
                  setMemberFilter(v);
                  setCurrentPage(1);
                }}
                options={members.map((member) => ({
                  value: member,
                  label: member,
                }))}
                leftIcon={<FiUser size={14} style={{ color: TEAMS_PURPLE, flexShrink: 0 }} aria-hidden />}
              />
            </div>
            <button
              type="button"
              onClick={handleReset}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                flexShrink: 0,
                height: 34,
                padding: '0 14px',
                border: `none`,
                background: 'rgb(254, 215, 215)',
                borderRadius: 6,
                cursor: 'pointer',
                fontSize: 12,
                fontWeight: 600,
                color: 'rgb(197, 48, 48)',
              }}
            >
              <FiX size={14} strokeWidth={2.25} aria-hidden style={{ color: 'rgb(197, 48, 48)' }} />
              Reset Filters
            </button>
          </div>
          {canAddTeamPermission && (
            <button
              type="button"
              onClick={() => navigate(`${appBasePath}/add-team`)}
              style={{ ...toolbarPrimaryBtnBase, background: PRIMARY }}
            >
              <FiPlus size={15} strokeWidth={2.5} aria-hidden style={{ color: '#fff' }} />
              Add Team
            </button>
          )}
          </div>
        </div>

        {dateRangeFilter === 'custom' && (
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
              style={{ padding: '10px 12px', border: '1px solid #d0d5dd', borderRadius: '10px', fontSize: '13px' }}
            />
            <input
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
              style={{ padding: '10px 12px', border: '1px solid #d0d5dd', borderRadius: '10px', fontSize: '13px' }}
            />
          </div>
        )}

        <div
          style={{
            marginTop: 18,
            overflowX: 'auto',
            borderRadius: 10,
            border: '1px solid #eaecf0',
            boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
          }}
        >
          <div
            style={{
              opacity: tableRefreshing ? 0.88 : 1,
              transition: 'opacity 0.2s ease-out',
              pointerEvents: tableRefreshing ? 'none' : 'auto',
            }}
          >
          <table style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}>
            <thead style={{ background: PRIMARY }}>
              <tr>
                <th
                  style={{
                    textAlign: 'center',
                    padding: '8px 10px',
                    color: '#ffffff',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  S.No
                </th>
                {renderSortableHeader('teamName', 'Team Name', 'Sort by team name')}
                {renderSortableHeader('teamLeader', 'Team Leader', 'Sort by team leader')}

                {showAvgRatingColumn
                  ? renderSortableHeader('avgRating', 'Avg. Rating', 'Sort by average rating')
                  : null}
                <th
                  style={{
                    textAlign: 'center',
                    padding: '8px 10px',
                    color: '#ffffff',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan={tableColumnCount} style={{ padding: '16px', textAlign: 'center', color: '#98a2b3', fontSize: '13px' }}>
                    Loading event teams...
                  </td>
                </tr>
              ) : paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan={tableColumnCount} style={{ padding: '16px', textAlign: 'center', color: '#98a2b3', fontSize: '13px' }}>
                    No events found for selected filters.
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row, index) => (
                  <tr
                    key={row.id}
                    style={{
                      borderTop: '1px solid #eaecf0',
                      background: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                    }}
                  >
                    <td style={{ padding: '7px 10px', fontSize: 12, color: '#344054', textAlign: 'center' }}>
                      {(safePage - 1) * itemsPerPage + index + 1}
                    </td>
                    <td style={{ padding: '7px 10px', fontSize: 12, color: '#101828', textAlign: 'left', fontWeight: 500 }}>
                      {row.teamName}
                    </td>
                    <td style={{ padding: '7px 10px', fontSize: 12, color: '#344054', textAlign: 'left' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span
                          aria-hidden
                          style={{
                            width: 24,
                            height: 24,
                            borderRadius: 6,
                            background: 'rgba(124, 58, 237, 0.12)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: TEAMS_PURPLE,
                            flexShrink: 0,
                          }}
                        >
                          <FiUser size={12} strokeWidth={2.25} />
                        </span>
                        {row.teamLeader}
                      </span>
                    </td>

                    {showAvgRatingColumn ? (
                      <td style={{ padding: '7px 10px', fontSize: 12, color: '#344054', textAlign: 'center' }}>
                        {Number.isFinite(row.avgRating) ? round1(row.avgRating)?.toFixed(1) : '-'}
                      </td>
                    ) : null}
                    <td style={{ padding: '7px 10px', textAlign: 'center' }}>
                      <div style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap', justifyContent: 'center' }}>
                      {canViewTeamDetails && (
                      <span className="icon-tooltip-wrap">
                        <button
                          type="button"
                          onClick={() => handleViewTeamClick(row)}
                          aria-label="View team details"
                          className="icon-action-btn"
                          style={{
                            border: '1px solid #93c5fd',
                            background: 'white',
                            borderRadius: '6px',
                            width: '24px',
                            height: '24px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#2563eb',
                            cursor: 'pointer',
                            opacity: 1,
                          }}
                        >
                          <FiEye size={12} />
                        </button>
                        <span className="icon-tooltip-bubble">View Team</span>
                      </span>
                      )}
                      {canEditEvent(row) && (
                      <span className="icon-tooltip-wrap">
                        <button
                          onClick={() => handleEditClick(row)}
                          aria-label="Edit team and event"
                          className="icon-action-btn"
                          style={{
                            border: '1px solid #fdba74',
                            background: 'white',
                            borderRadius: '6px',
                            width: '24px',
                            height: '24px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#d97706',
                            cursor: 'pointer',
                            opacity: 1,
                          }}
                        >
                          <FiEdit2 size={12} />
                        </button>
                        <span className="icon-tooltip-bubble">Edit</span>
                      </span>
                      )}
                      {canCopyTeamPermission && (
                        <span className="icon-tooltip-wrap">
                          <button
                            onClick={() => handleDuplicateClick(row)}
                            aria-label="Duplicate this event team"
                            className="icon-action-btn"
                            style={{
                              border: '1px solid #c4b5fd',
                              background: 'white',
                              borderRadius: '6px',
                              width: '24px',
                              height: '24px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#7c3aed',
                              cursor: 'pointer',
                              opacity: 1,
                            }}
                          >
                            <FiCopy size={12} />
                          </button>
                          <span className="icon-tooltip-bubble">Copy</span>
                        </span>
                      )}
                      {canUseFeedbackAction(row) && (
                      <span className="icon-tooltip-wrap">
                        <button
                          type="button"
                          onClick={() => handleFeedbackClick(row)}
                          aria-label="View Feedback"
                          className="icon-action-btn"
                          style={{
                            border: '1px solid #99f6e4',
                            background: 'white',
                            borderRadius: '6px',
                            width: '24px',
                            height: '24px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#0f766e',
                            cursor: 'pointer',
                            opacity: 1,
                          }}
                        >
                          <FiMessageSquare size={12} />
                        </button>
                        <span className="icon-tooltip-bubble">View Feedback</span>
                      </span>
                      )}
                      {canDeleteTeamPermission && canDeleteEvent(row) && (
                        <span className="icon-tooltip-wrap">
                          <button
                            onClick={() => handleDelete(row)}
                            aria-label="Delete this event team"
                            className="icon-action-btn"
                            style={{
                              border: '1px solid #fda4af',
                              color: '#e11d48',
                              background: '#ffffff',
                              borderRadius: '6px',
                              width: '24px',
                              height: '24px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              opacity: 1,
                            }}
                          >
                            <FiTrash2 size={12} />
                          </button>
                          <span className="icon-tooltip-bubble">Delete</span>
                        </span>
                      )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          </div>
        </div>

        <Pagination
          currentPage={safePage}
          totalPages={totalPages}
          totalItems={effectiveTotalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={(page) => {
            setCurrentPage(page);
            loadEventTeams({
              ...appliedFilters,
              page,
              limit: itemsPerPage,
              sortBy: sortConfig.key,
              sortOrder: sortConfig.direction,
            });
          }}
          onItemsPerPageChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
        />
      </div>



      {selectedTeam && (
        <div
          onClick={() => setSelectedTeam(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(2, 6, 23, 0.62)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px',
          }}
        >
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              background: '#ffffff',
              width: '100%',
              maxWidth: '460px',
              borderRadius: '14px',
              boxShadow: '0 20px 44px rgba(2, 6, 23, 0.2)',
              border: '1px solid #e8ecf0',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding: '12px 14px 11px',
                borderBottom: '1px solid #eef1f4',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                <div
                  aria-hidden
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    background: TEAM_MEMBER_MODAL_HEADER_TILE_BG,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: TEAM_MEMBER_MODAL_ACCENT,
                  }}
                >
                  <FiUsers size={18} strokeWidth={2} />
                </div>
                <h3 style={{ margin: 0, color: '#0f172a', fontSize: '16px', fontWeight: 700, letterSpacing: '-0.02em' }}>
                  Team Details
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedTeam(null)}
                aria-label="Close"
                style={{
                  border: '1px solid #e8ecf0',
                  background: '#f9fafb',
                  color: '#64748b',
                  borderRadius: '8px',
                  width: 32,
                  height: 32,
                  padding: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <FiX size={17} strokeWidth={2} />
              </button>
            </div>

            <div style={{ padding: '12px 14px 14px' }}>
              <div style={{ display: 'grid', gap: '8px', gridTemplateColumns: '1fr 1fr' }}>
                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e8ecf0',
                    borderRadius: '10px',
                    padding: '10px 12px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    boxSizing: 'border-box',
                  }}
                >
                  <div
                    aria-hidden
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      background: TEAM_MEMBER_MODAL_ICON_TILE_BG,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: TEAM_MEMBER_MODAL_ACCENT,
                    }}
                  >
                    <FiUsers size={16} strokeWidth={2} />
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: '10px',
                        color: '#64748b',
                        marginBottom: '3px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      Team Name
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', lineHeight: 1.35, wordBreak: 'break-word' }}>
                      {selectedTeam.teamName || '-'}
                    </div>
                  </div>
                </div>
                <div
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e8ecf0',
                    borderRadius: '10px',
                    padding: '10px 12px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '10px',
                    boxSizing: 'border-box',
                  }}
                >
                  <div
                    aria-hidden
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '8px',
                      background: TEAM_MEMBER_MODAL_ICON_TILE_BG,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: TEAM_MEMBER_MODAL_ACCENT,
                    }}
                  >
                    <FiAward size={16} strokeWidth={2} />
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontSize: '10px',
                        color: '#64748b',
                        marginBottom: '3px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                      }}
                    >
                      Team Leader
                    </div>
                    <div style={{ fontSize: '13px', fontWeight: 700, color: '#1e293b', lineHeight: 1.35, wordBreak: 'break-word' }}>
                      {selectedTeam.teamLeader || '-'}
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: '10px',
                  background: '#ffffff',
                  border: '1px solid #e8ecf0',
                  borderRadius: '10px',
                  overflow: 'hidden',
                }}
              >
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead
                    style={{
                      background: 'linear-gradient(135deg, rgb(102, 126, 234) 0%, rgb(118, 75, 162) 100%)',
                    }}
                  >
                    <tr>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px 10px',
                          fontSize: '11px',
                          color: '#f8fafc',
                          fontWeight: 600,
                          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                          borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                          borderTopLeftRadius: '10px',
                          verticalAlign: 'middle',
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <FiUsers size={13} strokeWidth={2} style={{ opacity: 0.95 }} aria-hidden />
                          Role
                        </span>
                      </th>
                      <th
                        style={{
                          textAlign: 'left',
                          padding: '8px 10px',
                          fontSize: '11px',
                          color: '#f8fafc',
                          fontWeight: 600,
                          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
                          borderTopRightRadius: '10px',
                          verticalAlign: 'middle',
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <FiUsers size={13} strokeWidth={2} style={{ opacity: 0.95 }} aria-hidden />
                          Member
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedTeam.teamMembers || []).length === 0 ? (
                      <tr>
                        <td colSpan="2" style={{ padding: '10px 8px', color: '#94a3b8', fontSize: '11px', textAlign: 'center' }}>
                          No members assigned.
                        </td>
                      </tr>
                    ) : (
                      selectedTeam.teamMembers.map((member, index) => {
                        const roleText = member.role || '-';
                        const RoleIcon = getRoleIconComponent(roleText);
                        const members = selectedTeam.teamMembers || [];
                        const isLast = index === members.length - 1;
                        return (
                          <tr key={`${member.role || 'role'}-${index}`} style={{ borderTop: '1px solid #eef1f4' }}>
                            <td
                              style={{
                                padding: '8px 10px',
                                fontSize: '12px',
                                color: '#334155',
                                borderRight: '1px solid #eef1f4',
                                verticalAlign: 'middle',
                                borderBottomLeftRadius: isLast ? '10px' : undefined,
                              }}
                            >
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                <RoleIcon size={15} strokeWidth={2} style={{ color: TEAM_MEMBER_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />
                                {roleText}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: '8px 10px',
                                fontSize: '12px',
                                color: '#334155',
                                verticalAlign: 'middle',
                                borderBottomRightRadius: isLast ? '10px' : undefined,
                              }}
                            >
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                                <FiUser size={15} strokeWidth={2} style={{ color: TEAM_MEMBER_MODAL_ACCENT, flexShrink: 0 }} aria-hidden />
                                {resolveMemberName(member)}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmActionModal
        isOpen={Boolean(deleteCandidate)}
        onClose={() => {
          if (!deleteLoading) setDeleteCandidate(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Event Team"
        message={`Are you sure you want to delete "${deleteCandidate?.eventName || 'this event team'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        loading={deleteLoading}
      />
    </div>
  );
};

export default TeamsEventsPage;
